// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { horizontalBars } from '../components/charts';
import { bySector, rankedBy } from '../analysis';
import { OVER, UNDER, sectorColour } from '../sectors';
import { escapeHtml, formatCompact, formatNumber } from '../utils/format';
import { glossaryTerm } from '../glossary';

const mt = (t: number) => `${(t / 1e6).toFixed(2)}M`;

export function renderMarket(root: HTMLElement, ctx: ViewContext): void {
  const { facilities, meta } = ctx.data;
  const n = meta.national;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The carbon market inside the scheme</h1>
      <p class="view-sub">
        A facility under its ${glossaryTerm('baseline', 'baseline')} earns ${glossaryTerm('smc', 'Safeguard Mechanism Credits')};
        one over it must surrender credits to make up the gap — either those SMCs, bought from a cleaner facility, or
        ${glossaryTerm('accu', 'ACCUs')} bought from carbon-offset projects elsewhere in the economy. This is where the
        scheme leans on offsets rather than on-site cuts.
      </p>
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title">Credits earned vs surrendered</div>
        <div class="panel-sub">Facilities beating baseline earned ${mt(n.smcsIssued)} SMCs; those over the line surrendered ${mt(n.unitsSurrendered)} credits to comply.</div>
        <div class="mix-bar tall" data-role="earned" style="margin-top:var(--space-md)"></div>
        <div class="legend" style="margin-top:var(--space-xs)"><span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${UNDER}"></span>SMCs issued (earned) ${formatNumber(n.smcsIssued)}</span></div>
        <div class="mix-bar tall" data-role="surrendered" style="margin-top:var(--space-lg)"></div>
        <div class="legend" style="margin-top:var(--space-xs)">
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:#b45309"></span>ACCUs surrendered ${formatNumber(n.accusSurrendered)}</span>
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OVER}"></span>SMCs surrendered ${formatNumber(n.smcsSurrendered)}</span>
        </div>
        <div class="callout" style="margin-top:var(--space-lg)">
          <strong>${(n.unitsSurrendered / Math.max(n.smcsIssued, 1)).toFixed(1)}× more credits were surrendered than earned.</strong>
          Most of the gap is ACCUs — carbon offsets bought from outside the scheme — meaning much of the "reduction" happened
          elsewhere, not at these facilities.
        </div>
      </div>
      <div class="panel">
        <div class="stat-grid" style="margin-bottom:0;grid-template-columns:repeat(2,1fr)">
          <div class="stat"><div class="stat-label">SMCs issued</div><div class="stat-value under">${formatCompact(n.smcsIssued)}</div><div class="stat-note">credits earned by beating baseline</div></div>
          <div class="stat"><div class="stat-label">Credits surrendered</div><div class="stat-value over">${formatCompact(n.unitsSurrendered)}</div><div class="stat-note">to cover excess emissions</div></div>
          <div class="stat"><div class="stat-label">ACCUs surrendered</div><div class="stat-value slate">${formatCompact(n.accusSurrendered)}</div><div class="stat-note">offsets from outside the scheme</div></div>
          <div class="stat"><div class="stat-label">SMCs surrendered</div><div class="stat-value slate">${formatCompact(n.smcsSurrendered)}</div><div class="stat-note">traded between facilities</div></div>
        </div>
        <div class="panel-title" style="margin-top:var(--space-lg)">Credits earned vs surrendered, by industry</div>
        <div class="panel-sub">Net position of each industry in the credit market.</div>
        <div class="table-scroll" style="max-height:300px;overflow-y:auto">
          <table class="data">
            <thead><tr><th>Industry</th><th class="t-num">Earned</th><th class="t-num">Surrendered</th></tr></thead>
            <tbody data-role="sectorrows"></tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="grid-2 wide" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-title" style="color:${OVER}">Biggest credit surrenderers</div>
        <div class="panel-sub">Facilities that surrendered the most ACCUs and SMCs to cover emissions over baseline. Click to open.</div>
        <div class="chart-scroll" data-role="buyers"></div>
      </div>
      <div class="panel">
        <div class="panel-title" style="color:${UNDER}">Biggest credit earners</div>
        <div class="panel-sub">Facilities issued the most Safeguard Mechanism Credits for coming in under baseline. Click to open.</div>
        <div class="chart-scroll" data-role="sellers"></div>
      </div>
    </div>
  `;

  const earned = root.querySelector('[data-role="earned"]') as HTMLElement;
  earned.innerHTML = `<div class="mix-seg" style="flex:${Math.max(n.smcsIssued, 1)};background:${UNDER}" data-tip="SMCs issued: ${formatNumber(n.smcsIssued)}"></div>`;
  const surrendered = root.querySelector('[data-role="surrendered"]') as HTMLElement;
  surrendered.innerHTML = [
    { c: '#b45309', v: n.accusSurrendered, l: `ACCUs surrendered: ${formatNumber(n.accusSurrendered)}` },
    { c: OVER, v: n.smcsSurrendered, l: `SMCs surrendered: ${formatNumber(n.smcsSurrendered)}` },
  ]
    .filter((s) => s.v > 0)
    .map((s) => `<div class="mix-seg" style="flex:${s.v};background:${s.c}" data-tip="${escapeHtml(s.l)}"></div>`)
    .join('');
  // Keep the two bars on a comparable scale by padding the shorter with an empty spacer.
  const scaleMax = Math.max(n.smcsIssued, n.unitsSurrendered) || 1;
  if (n.smcsIssued < scaleMax) earned.innerHTML += `<div class="mix-seg" style="flex:${scaleMax - n.smcsIssued};background:transparent"></div>`;

  // Sector credit table.
  const sectorRows = root.querySelector('[data-role="sectorrows"]') as HTMLElement;
  const sectors = bySector(facilities).filter((s) => s.smcsIssued > 0 || s.unitsSurrendered > 0).sort((a, b) => b.unitsSurrendered - a.unitsSurrendered);
  sectorRows.innerHTML = sectors
    .map(
      (s) => `<tr style="cursor:default">
        <td><span class="legend-swatch" style="display:inline-block;background:${sectorColour(s.label)};margin-right:6px;vertical-align:middle"></span>${escapeHtml(s.label)}</td>
        <td class="t-num" style="color:${UNDER}">${formatCompact(s.smcsIssued)}</td>
        <td class="t-num" style="color:${OVER}">${formatCompact(s.unitsSurrendered)}</td>
      </tr>`,
    )
    .join('');

  // Leaderboards.
  const buyers = rankedBy(facilities, 'unitsSurrendered').filter((f) => f.unitsSurrendered > 0).slice(0, 12);
  const sellers = rankedBy(facilities, 'smcsIssued').filter((f) => f.smcsIssued > 0).slice(0, 12);
  (root.querySelector('[data-role="buyers"]') as HTMLElement).appendChild(
    horizontalBars(
      buyers.map((f) => ({ id: f.slug, label: f.name.length > 24 ? f.name.slice(0, 23) + '…' : f.name, value: f.unitsSurrendered, colour: OVER, tip: `${f.name}\n${f.company}\n${formatNumber(f.unitsSurrendered)} credits surrendered` })),
      { width: 560, labelWidth: 190, valueFormat: (v) => formatCompact(v), onClick: (id) => ctx.openFacility(id) },
    ),
  );
  (root.querySelector('[data-role="sellers"]') as HTMLElement).appendChild(
    horizontalBars(
      sellers.map((f) => ({ id: f.slug, label: f.name.length > 24 ? f.name.slice(0, 23) + '…' : f.name, value: f.smcsIssued, colour: UNDER, tip: `${f.name}\n${f.company}\n${formatNumber(f.smcsIssued)} SMCs earned` })),
      { width: 560, labelWidth: 190, valueFormat: (v) => formatCompact(v), onClick: (id) => ctx.openFacility(id) },
    ),
  );
}
